import { useEffect } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import { useConsent } from "@/hooks/useConsent";
import { setAnalyticsConsent } from "@/lib/analytics";

const isDevAuthBypass = ((import.meta as any).env as any).VITE_DEV_AUTH_BYPASS === "true";

// Mock hooks for bypass mode - return safe default values
const mockUseAuth = () => ({ isSignedIn: false, userId: null });
const mockUseUser = () => ({ user: null });

// Use real or mock hooks based on bypass mode
const safeUseAuth = isDevAuthBypass ? mockUseAuth : useAuth;
const safeUseUser = isDevAuthBypass ? mockUseUser : useUser;

declare global {
	interface Window {
		gtag?: (...args: any[]) => void;
	}
}

export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
	const { isSignedIn, userId } = safeUseAuth();
	const { user } = safeUseUser();
	const { analyticsGranted } = useConsent();

	// Bridge consent into the (non-component) analytics layer so ReactGA is only
	// initialized — and events only sent — after the user grants consent.
	useEffect(() => {
		setAnalyticsConsent(analyticsGranted);
	}, [analyticsGranted]);

	useEffect(() => {
		if (typeof window === "undefined") return;
		if (!window.gtag) return;
		if (!analyticsGranted) return;

		if (isSignedIn && userId) {
			window.gtag("config", ((import.meta as any).env as any).VITE_GA_MEASUREMENT_ID || "", {
				user_id: userId,
			});

			if (user) {
				const emailDomain =
					user.primaryEmailAddress?.emailAddress?.split("@")[1] || "unknown";

				window.gtag("set", "user_properties", {
					user_clerk_id: userId,
					user_email_domain: emailDomain,
				});
			}
		} else if (!isSignedIn && userId === null) {
			window.gtag("config", ((import.meta as any).env as any).VITE_GA_MEASUREMENT_ID || "", {
				user_id: null,
			});
		}
	}, [isSignedIn, userId, user, analyticsGranted]);

	return <>{children}</>;
}
